import {
  configureStore,
  combineReducers,
  type UnknownAction,
  type Reducer,
} from '@reduxjs/toolkit';
import authReducer from '../auth/slices/authSlice';
import { baseApi } from '../api/baseApi';
import { persistStore, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import type { AuthState } from '../auth/slices/authSlice';
import { createPersistConfig, persistSliceReducer } from './persistConfig';

const authPersistConfig = createPersistConfig<AuthState>('auth', [
  'token',
  'partner',
  'allowedStores',
  'activeStoreId',
  'theme',
  'userType',
  'isSuperadmin',
  'role',
  'permissions',
  'allowedPermissions',
]);

const baseReducerMap = {
  auth: persistSliceReducer(authReducer, authPersistConfig),
  [baseApi.reducerPath]: baseApi.reducer,
};

export type RootState = {
  auth: AuthState;
  [baseApi.reducerPath]: ReturnType<typeof baseApi.reducer>;
  storefront?: any;
  tenant?: any;
  admin?: any;
};

export const createBaseStore = <ExtraReducers extends Record<string, any>>(
  extraReducers: ExtraReducers,
  extraPersistConfigs: Record<
    keyof ExtraReducers,
    ReturnType<typeof createPersistConfig<any>>
  > = {} as any
) => {
  const extraReducerEntries = Object.entries(extraReducers).map(([key, reducer]) => {
    const persistConfig = extraPersistConfigs[key as keyof ExtraReducers];
    if (persistConfig) {
      return [key, persistSliceReducer(reducer, persistConfig)] as const;
    }
    return [key, reducer] as const;
  });

  const combinedReducer = combineReducers({
    ...baseReducerMap,
    ...Object.fromEntries(extraReducerEntries),
  });

  type CombinedState = ReturnType<typeof combinedReducer>;

  const finalRootReducer: Reducer<CombinedState, UnknownAction> = (
    state,
    action
  ): CombinedState => {
    if (action.type === 'auth/logout') {
      state = undefined as any;
      storage.removeItem('persist:sama_auth');
      Object.keys(extraPersistConfigs).forEach((key) => {
        storage.removeItem(`persist:sama_${key}`);
      });
    }
    return combinedReducer(state as any, action) as CombinedState;
  };

  const store = configureStore({
    reducer: finalRootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(baseApi.middleware),
  });

  const persistor = persistStore(store, null, () => {
    // Persistor ready - can add sync logic here if needed
  });

  return { store, persistor };
};

export type AppDispatch = ReturnType<typeof configureStore>['dispatch'];
